/**
 * Orich Theme — mobile nav, predictive search, section utilities
 */
(function () {
  'use strict';

  let searchDebounce;

  const STICKY_HEADER_SCROLL_THRESHOLD = 0.1;
  let stickyHeaderTicking = false;

  function getScrollPercent() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 0;
    return window.scrollY / scrollable;
  }

  function syncStickyHeaderSpacer() {
    const announcement = document.querySelector('[data-announcement-bar]');
    const announcementSticky = announcement?.hasAttribute('data-announcement-sticky');
    const announcementSpacer = document.querySelector('[data-announcement-spacer]');
    const siteHeader = document.querySelector('[data-site-header]');
    const spacer = document.querySelector('[data-header-spacer]');

    const announcementHeight = announcement
      ? Math.ceil(announcement.getBoundingClientRect().height)
      : 0;

    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      announcementSticky && announcementHeight > 0 ? `${announcementHeight}px` : '0px'
    );

    if (announcementSpacer) {
      announcementSpacer.style.height = `${announcementHeight}px`;
    }

    if (!siteHeader) return;

    const shouldStick = getScrollPercent() >= STICKY_HEADER_SCROLL_THRESHOLD;
    siteHeader.classList.toggle('is-stuck', shouldStick);

    if (shouldStick && announcementSticky && announcementHeight > 0) {
      siteHeader.setAttribute('data-header-below-announcement', '');
    } else {
      siteHeader.removeAttribute('data-header-below-announcement');
    }

    const headerHeight = Math.ceil(siteHeader.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--site-header-height', `${headerHeight}px`);

    if (spacer) {
      spacer.style.height = shouldStick ? `${headerHeight}px` : '0px';
    }
  }

  function onStickyHeaderScroll() {
    if (stickyHeaderTicking) return;
    stickyHeaderTicking = true;
    window.requestAnimationFrame(() => {
      syncStickyHeaderSpacer();
      stickyHeaderTicking = false;
    });
  }

  syncStickyHeaderSpacer();
  window.addEventListener('resize', syncStickyHeaderSpacer);
  window.addEventListener('scroll', onStickyHeaderScroll, { passive: true });
  document.addEventListener('shopify:section:load', syncStickyHeaderSpacer);
  document.addEventListener('shopify:section:unload', syncStickyHeaderSpacer);
  document.addEventListener('shopify:section:reorder', syncStickyHeaderSpacer);
  document.addEventListener('shopify:section:select', syncStickyHeaderSpacer);
  document.addEventListener('shopify:section:deselect', syncStickyHeaderSpacer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeMobileNav();
      closePredictiveSearch();
      return;
    }

    const gallery = document.querySelector('[data-product-gallery]');
    if (gallery && document.activeElement?.closest('[data-media-thumb]')) {
      const thumbs = Array.from(gallery.querySelectorAll('[data-media-thumb]'));
      const idx = thumbs.indexOf(document.activeElement);
      if (idx === -1) return;
      let next = idx;
      if (e.key === 'ArrowRight') next = Math.min(thumbs.length - 1, idx + 1);
      else if (e.key === 'ArrowLeft') next = Math.max(0, idx - 1);
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = thumbs.length - 1;
      else return;
      e.preventDefault();
      thumbs[next].focus();
      thumbs[next].click();
    }
  });

  function closeMobileNav(options = {}) {
    const restoreFocus = options.restoreFocus !== false;
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!nav || nav.getAttribute('aria-hidden') !== 'false') {
      document.body.style.overflow = '';
      return;
    }
    nav.setAttribute('aria-hidden', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (restoreFocus && toggle && typeof toggle.focus === 'function') {
      toggle.focus();
    }
  }

  function openMobileNav() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('[data-mobile-nav]');
    if (!nav) return;
    nav.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    const closeBtn = nav.querySelector('[data-mobile-menu-close][aria-label]');
    if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
  }

  function closePredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach((panel) => {
      panel.setAttribute('aria-hidden', 'true');
      const toggle = panel.closest('[data-predictive-search-wrap]')?.querySelector('[data-search-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  }

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-media-thumb]');
    if (thumb) {
      const main = document.querySelector('#ProductImage');
      const src = thumb.dataset.src;
      const srcset = thumb.dataset.srcset;
      if (main && src) {
        main.src = src;
        if (srcset) main.srcset = srcset;
        else main.removeAttribute('srcset');
        if (main.sizes) main.sizes = '(max-width: 1024px) 100vw, 50vw';
      }
      thumb.closest('[data-product-gallery]')?.querySelectorAll('[data-media-thumb]').forEach((btn) => {
        const active = btn === thumb;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      return;
    }

    const productQtyBtn = e.target.closest('[data-product-qty-change]');
    if (productQtyBtn) {
      e.preventDefault();
      const form = productQtyBtn.closest('[data-product-form]');
      const input = form?.querySelector('[data-product-qty]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const delta = parseInt(productQtyBtn.dataset.productQtyChange, 10);
      const current = parseInt(input.value, 10) || min;
      input.value = Math.max(min, current + delta);
      syncStickyQtyDisplay(form.closest('[data-section-id]'));
      return;
    }

    const stickyQtyBtn = e.target.closest('[data-sticky-qty-change]');
    if (stickyQtyBtn) {
      e.preventDefault();
      const section = stickyQtyBtn.closest('[data-section-id]');
      const form = section?.querySelector('[data-product-form]');
      const input = form?.querySelector('[data-product-qty]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const delta = parseInt(stickyQtyBtn.dataset.stickyQtyChange, 10);
      const current = parseInt(input.value, 10) || min;
      input.value = Math.max(min, current + delta);
      syncStickyQtyDisplay(section);
      return;
    }

    const stickyAddBtn = e.target.closest('[data-sticky-add-to-cart]');
    if (stickyAddBtn) {
      e.preventDefault();
      const form = stickyAddBtn.closest('[data-section-id]')?.querySelector('[data-product-form]');
      if (!form || stickyAddBtn.disabled) return;
      form.requestSubmit();
      return;
    }

    const optionPill = e.target.closest('[data-option-pill]');
    if (optionPill) {
      e.preventDefault();
      const form = optionPill.closest('[data-product-form]');
      const picker = optionPill.closest('[data-variant-picker]');
      if (!form || !picker) return;

      const optionGroup = optionPill.closest('[data-product-option]');
      optionGroup?.querySelectorAll('[data-option-pill]').forEach((pill) => {
        const active = pill === optionPill;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-pressed', String(active));
      });

      applyOptionSelection(form, picker);
      return;
    }

    const menuToggle = e.target.closest('[data-mobile-menu-toggle]');
    if (menuToggle) {
      e.preventDefault();
      const nav = document.querySelector('[data-mobile-nav]');
      const expanded = nav?.getAttribute('aria-hidden') === 'false';
      if (expanded) closeMobileNav();
      else openMobileNav();
      return;
    }

    if (e.target.closest('[data-mobile-menu-close]')) {
      const closer = e.target.closest('[data-mobile-menu-close]');
      const isLink = closer.tagName === 'A' && closer.getAttribute('href');
      if (!isLink) e.preventDefault();
      // Don't restore focus when navigating via a link — focus() cancels navigation.
      closeMobileNav({ restoreFocus: !isLink });
      return;
    }

    const searchToggle = e.target.closest('[data-search-toggle]');
    if (searchToggle) {
      const wrap = searchToggle.closest('[data-predictive-search-wrap]');
      const panel = wrap?.querySelector('[data-predictive-search]');
      if (!panel) return;
      const expanded = searchToggle.getAttribute('aria-expanded') === 'true';
      const next = !expanded;
      searchToggle.setAttribute('aria-expanded', String(next));
      panel.setAttribute('aria-hidden', String(!next));
      if (next) {
        const input = panel.querySelector('[data-predictive-search-input]');
        if (input) input.focus();
      }
      return;
    }

    if (!e.target.closest('[data-predictive-search-wrap]')) {
      document.querySelectorAll('[data-predictive-search]').forEach((panel) => {
        panel.setAttribute('aria-hidden', 'true');
        const toggle = panel.closest('[data-predictive-search-wrap]')?.querySelector('[data-search-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    }

    const faqToggle = e.target.closest('[data-faq-toggle]');
    if (faqToggle) {
      const item = faqToggle.closest('[data-faq-item]');
      if (!item) return;
      const wasOpen = item.classList.contains('is-open');
      item.closest('[data-faq-list]')?.querySelectorAll('[data-faq-item]').forEach((el) => {
        el.classList.remove('is-open');
        el.querySelector('[data-faq-toggle]')?.setAttribute('aria-expanded', 'false');
      });
      if (!wasOpen) {
        item.classList.add('is-open');
        faqToggle.setAttribute('aria-expanded', 'true');
      }
    }

    const carouselPrev = e.target.closest('[data-carousel-prev], [data-ugc-prev]');
    const carouselNext = e.target.closest('[data-carousel-next], [data-ugc-next]');
    if (carouselPrev || carouselNext) {
      const section = (carouselPrev || carouselNext).closest('section');
      const track = section?.querySelector('[data-carousel-track], [data-ugc-track]');
      if (!track) return;

      if (track.hasAttribute('data-video-testimonial-track') && track.hasAttribute('data-carousel-loop')) {
        scrollVideoTestimonialCarousel(track, Boolean(carouselNext));
        return;
      }

      let scrollAmount;
      if (track.hasAttribute('data-video-testimonial-track')) {
        const slide = track.querySelector('[data-carousel-slide]');
        scrollAmount = slide?.offsetWidth || track.clientWidth;
      } else {
        const card = track.querySelector('[data-ugc-card]');
        scrollAmount = card ? card.offsetWidth + 20 : 320;
      }

      track.scrollBy({
        left: carouselPrev ? -scrollAmount : scrollAmount,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      return;
    }

    const videoPlayBtn = e.target.closest('[data-video-testimonial-play]');
    if (videoPlayBtn) {
      e.preventDefault();
      e.stopPropagation();
      toggleVideoTestimonial(videoPlayBtn.closest('[data-video-testimonial-card]'));
      return;
    }

    const videoMedia = e.target.closest('[data-video-testimonial-media]');
    if (videoMedia?.closest('[data-video-testimonial-card].is-playing')) {
      pauseVideoTestimonial(videoMedia.closest('[data-video-testimonial-card]'));
    }
  });

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-predictive-search-input]');
    if (!input || !window.themeSettings?.predictiveSearchEnabled) return;
    clearTimeout(searchDebounce);
    const query = input.value.trim();
    const results = input.closest('[data-predictive-search]')?.querySelector('[data-predictive-search-results]');
    if (!results) return;
    if (query.length < 2) {
      results.innerHTML = '';
      return;
    }
    searchDebounce = setTimeout(() => fetchPredictiveSearch(query, results), 250);
  });

  async function fetchPredictiveSearch(query, resultsEl) {
    const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
    try {
      resultsEl.innerHTML = `<p class="text-sm text-muted-foreground px-2 py-3">${window.searchStrings?.loading || 'Loading…'}</p>`;
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.shopify-section') || doc;
      const items = section.querySelectorAll('[data-predictive-search-result]');
      const empty = section.querySelector('[data-predictive-search-empty]');
      if (empty) {
        resultsEl.innerHTML = empty.outerHTML;
        return;
      }
      if (!items.length) {
        resultsEl.innerHTML = `<p class="text-sm text-muted-foreground px-2 py-3">${window.searchStrings?.noResults || 'No results found'}</p>`;
        return;
      }
      const viewAll = section.querySelector('a[href*="search"]');
      resultsEl.innerHTML = Array.from(items).map((item) => item.closest('li')?.outerHTML || item.outerHTML).join('') + (viewAll ? viewAll.outerHTML : '');
    } catch (err) {
      console.error('[Search]', err);
    }
  }

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
    }

    connectedCallback() {
      if (this.loaded) return;
      const url = this.dataset.url;
      if (!url) return;
      this.loaded = true;
      fetch(url)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = doc.querySelector('[data-product-recommendations-grid]');
          const loading = this.querySelector('[data-recommendations-loading]');
          if (loading) loading.remove();
          if (grid && grid.children.length > 0) {
            this.innerHTML = grid.outerHTML;
          } else {
            this.closest('section')?.remove();
          }
        })
        .catch((err) => {
          console.error('[Recommendations]', err);
          this.closest('section')?.remove();
        });
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  function initProductDescriptions() {
    document.querySelectorAll('[data-product-description]').forEach((wrap) => {
      const content = wrap.querySelector('[data-product-description-content]');
      const toggle = wrap.querySelector('[data-product-description-toggle]');
      if (!content || !toggle || toggle.dataset.bound === 'true') return;

      toggle.dataset.bound = 'true';

      const syncToggle = () => {
        if (content.classList.contains('is-expanded')) {
          toggle.hidden = false;
          toggle.textContent = window.productStrings?.readLess || 'Read less';
          return;
        }

        toggle.textContent = window.productStrings?.readMore || 'Read more';
        toggle.hidden = content.scrollHeight <= content.clientHeight + 2;
      };

      syncToggle();
      window.addEventListener('resize', syncToggle);

      toggle.addEventListener('click', () => {
        content.classList.toggle('is-expanded');
        syncToggle();
      });
    });
  }

  initProductDescriptions();
  document.addEventListener('shopify:section:load', initProductDescriptions);

  function syncStickyQtyDisplay(section) {
    const form = section?.querySelector('[data-product-form]');
    const qtyInput = form?.querySelector('[data-product-qty]');
    const stickyQty = section?.querySelector('[data-sticky-qty]');
    if (qtyInput && stickyQty) stickyQty.textContent = qtyInput.value;
  }

  function syncStickyBar(section, { price, comparePrice, variantTitle, available }) {
    const stickyPrice = section?.querySelector('[data-sticky-price]');
    const stickyCompare = section?.querySelector('[data-sticky-compare-price]');
    const stickyVariant = section?.querySelector('[data-sticky-variant]');
    const stickyAddBtn = section?.querySelector('[data-sticky-add-to-cart]');
    const stickyLabel = section?.querySelector('[data-sticky-add-to-cart-label]');

    if (stickyPrice && price) stickyPrice.textContent = price;
    if (stickyCompare) {
      if (comparePrice) {
        stickyCompare.textContent = comparePrice;
        stickyCompare.hidden = false;
      } else {
        stickyCompare.textContent = '';
        stickyCompare.hidden = true;
      }
    }
    if (stickyVariant && variantTitle) stickyVariant.textContent = variantTitle;
    if (stickyAddBtn) stickyAddBtn.disabled = !available;
    if (stickyLabel) {
      stickyLabel.textContent = available
        ? (window.productStrings?.stickyAddToCart || 'Add to Cart')
        : (window.productStrings?.soldOut || 'Sold out');
    }
  }

  function initProductStickyCart() {
    document.querySelectorAll('[data-section-id]').forEach((section) => {
      if (!section.querySelector('[data-product-form]')) return;
      if (section.dataset.stickyCartBound === 'true') return;
      section.dataset.stickyCartBound = 'true';

      const sticky = section.querySelector('[data-product-sticky-cart]');
      const anchor = section.querySelector('[data-product-purchase]');
      const form = section.querySelector('[data-product-form]');
      if (!sticky || !anchor || !form) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
        },
        { threshold: 0 }
      );
      observer.observe(anchor);

      const qtyInput = form.querySelector('[data-product-qty]');
      qtyInput?.addEventListener('input', () => syncStickyQtyDisplay(section));
      qtyInput?.addEventListener('change', () => syncStickyQtyDisplay(section));
      syncStickyQtyDisplay(section);
    });
  }

  initProductStickyCart();
  document.addEventListener('shopify:section:load', initProductStickyCart);

  function getVideoTestimonialScrollStep(track) {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      const card = track.querySelector('[data-video-testimonial-card]');
      if (!card) return track.clientWidth;
      const trackGap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0');
      return card.offsetWidth + trackGap;
    }

    const slide = track.querySelector('[data-carousel-slide]');
    return slide?.offsetWidth || track.clientWidth;
  }

  function scrollVideoTestimonialCarousel(track, goNext) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const step = getVideoTestimonialScrollStep(track);

    if (isMobile) {
      const cards = Array.from(track.querySelectorAll('[data-video-testimonial-card]')).filter(
        (card) => !card.closest('[data-carousel-pad]')
      );
      if (!cards.length || !step) return;

      const currentIndex = Math.round(track.scrollLeft / step);
      let nextIndex = goNext ? currentIndex + 1 : currentIndex - 1;
      if (nextIndex >= cards.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = cards.length - 1;
      track.scrollTo({ left: nextIndex * step, behavior });
      return;
    }

    const slides = Array.from(track.querySelectorAll('[data-carousel-slide]'));
    if (!slides.length || !step) return;

    const currentIndex = Math.round(track.scrollLeft / step);
    let nextIndex = goNext ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex >= slides.length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = slides.length - 1;
    track.scrollTo({ left: nextIndex * step, behavior });
  }

  function initVideoTestimonialCarousels() {
    document.querySelectorAll('[data-video-testimonial-track][data-carousel-loop]').forEach((track) => {
      if (track.dataset.loopBound === 'true') return;
      track.dataset.loopBound = 'true';

      track.querySelectorAll('video').forEach((video) => {
        video.loop = true;
      });
    });
  }

  initVideoTestimonialCarousels();
  document.addEventListener('shopify:section:load', initVideoTestimonialCarousels);

  function initProductOptionPickers() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const picker = form.querySelector('[data-variant-picker]');
      if (!picker || picker.dataset.optionsBound === 'true') return;
      picker.dataset.optionsBound = 'true';
      applyOptionSelection(form, picker);
    });
  }

  initProductOptionPickers();
  document.addEventListener('shopify:section:load', initProductOptionPickers);

  function pauseVideoTestimonial(card) {
    const video = card?.querySelector('video');
    if (!video) return;
    video.pause();
    video.muted = true;
    card.classList.remove('is-playing');
  }

  function toggleVideoTestimonial(card) {
    if (!card) return;
    const video = card.querySelector('video');
    if (!video) return;

    document.querySelectorAll('[data-video-testimonial-card]').forEach((otherCard) => {
      if (otherCard !== card) pauseVideoTestimonial(otherCard);
    });

    if (!video.paused) {
      pauseVideoTestimonial(card);
      return;
    }

    video.loop = true;
    video.muted = false;
    video.volume = 1;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          card.classList.add('is-playing');
        })
        .catch(() => {
          video.muted = true;
          card.classList.remove('is-playing');
        });
    } else {
      card.classList.add('is-playing');
    }
  }

  function getProductVariants(picker) {
    const script = picker?.querySelector('[data-product-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('[Variants]', err);
      return [];
    }
  }

  function getSelectedOptions(picker) {
    const selected = [null, null, null];
    picker.querySelectorAll('[data-product-option]').forEach((group) => {
      const index = parseInt(group.dataset.optionIndex, 10);
      const active = group.querySelector('[data-option-pill].is-active');
      if (!Number.isNaN(index) && active) selected[index] = active.dataset.optionValue;
    });
    return selected;
  }

  function findMatchingVariant(variants, selectedOptions) {
    return variants.find((variant) => {
      return selectedOptions.every((value, index) => {
        if (value == null) return true;
        const key = `option${index + 1}`;
        return variant[key] == null || variant[key] === value;
      });
    }) || null;
  }

  function updateOptionAvailability(picker, variants, selectedOptions) {
    picker.querySelectorAll('[data-product-option]').forEach((group) => {
      const optionIndex = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('[data-option-pill]').forEach((pill) => {
        const value = pill.dataset.optionValue;
        const available = variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return selectedOptions.every((selectedValue, index) => {
            if (index === optionIndex || selectedValue == null) return true;
            return variant[`option${index + 1}`] === selectedValue;
          });
        });
        pill.classList.toggle('is-unavailable', !available);
        const label = pill.querySelector('span');
        if (label) label.classList.toggle('line-through', !available);
      });
    });
  }

  function applyOptionSelection(form, picker) {
    const variants = getProductVariants(picker);
    const selectedOptions = getSelectedOptions(picker);
    const variant = findMatchingVariant(variants, selectedOptions);
    updateOptionAvailability(picker, variants, selectedOptions);
    if (!variant) return;

    applyVariantSelection(form, {
      dataset: {
        variantId: String(variant.id),
        variantPrice: variant.price || '',
        variantComparePrice: variant.compare_at_price || '',
        variantTitle: variant.title || '',
        available: String(variant.available),
      },
    });
  }

  function applyVariantSelection(form, source) {
    const section = form.closest('[data-section-id]');
    const variantId = source.dataset.variantId;
    const price = source.dataset.variantPrice;
    const comparePrice = source.dataset.variantComparePrice;
    const variantTitle = source.dataset.variantTitle;
    const available = source.dataset.available === 'true';
    const idInput = form.querySelector('[name="id"]');
    const priceEl = section?.querySelector('[data-product-price]');
    const compareEl = section?.querySelector('[data-product-compare-price]');

    if (idInput && variantId) idInput.value = variantId;
    if (priceEl && price) priceEl.textContent = price;

    if (compareEl) {
      if (comparePrice) {
        compareEl.textContent = comparePrice;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    const inStockBadge = section?.querySelector('[data-product-stock-in]');
    const soldOutBadge = section?.querySelector('[data-product-stock-out]');
    if (inStockBadge) inStockBadge.hidden = !available;
    if (soldOutBadge) soldOutBadge.hidden = available;

    const addBtn = form.querySelector('[type="submit"]');
    if (addBtn) {
      addBtn.disabled = !available;
      const label = addBtn.querySelector('[data-add-to-cart-label]');
      const text = available
        ? (window.productStrings?.addToCart || 'Add to Cart')
        : (window.productStrings?.soldOut || 'Sold out');
      if (label) label.textContent = text;
    }

    syncStickyBar(section, { price, comparePrice, variantTitle, available });
  }

  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-collection-sort]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
      return;
    }

    const picker = e.target.closest('[data-variant-select]');
    if (!picker) return;
    const form = picker.closest('[data-product-form]');
    if (!form) return;
    const selected = picker.options[picker.selectedIndex];
    applyVariantSelection(form, selected);
  });

  function initHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach((root) => {
      if (root.dataset.heroBound === 'true') return;

      const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
      const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
      const prevBtn = root.querySelector('[data-hero-prev]');
      const nextBtn = root.querySelector('[data-hero-next]');
      if (slides.length < 2) return;

      root.dataset.heroBound = 'true';

      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      if (index < 0) index = 0;

      const enableAutoplay = root.dataset.autoplay === 'true';
      const interval = parseInt(root.dataset.interval || '5000', 10);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let timer = null;

      const show = (nextIndex) => {
        index = ((nextIndex % slides.length) + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };

      const start = () => {
        stop();
        if (!enableAutoplay || reduceMotion) return;
        timer = setInterval(() => show(index + 1), interval);
      };

      const goPrev = (event) => {
        event.preventDefault();
        event.stopPropagation();
        show(index - 1);
        start();
      };

      const goNext = (event) => {
        event.preventDefault();
        event.stopPropagation();
        show(index + 1);
        start();
      };

      prevBtn?.addEventListener('click', goPrev);
      nextBtn?.addEventListener('click', goNext);

      dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const target = parseInt(dot.dataset.index || '0', 10);
          show(target);
          start();
        });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) start();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else start();
      });

      show(index);
      start();
    });
  }

  initHeroSliders();
  document.addEventListener('shopify:section:load', initHeroSliders);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.('[data-hero-slider]') || event.target;
    if (root?.dataset) delete root.dataset.heroBound;
  });

  function initRecipeSliders(scope) {
    const roots = (scope || document).querySelectorAll('[data-recipe-slider]');
    roots.forEach((root) => {
      if (root.dataset.recipeBound === 'true') return;

      const track = root.querySelector('[data-recipe-track]');
      const items = Array.from(root.querySelectorAll('[data-recipe-item]'));
      const tabs = Array.from(root.querySelectorAll('[data-recipe-tab]'));
      const empty = root.querySelector('[data-recipe-empty]');
      const prevBtn = root.querySelector('[data-recipe-prev]');
      const nextBtn = root.querySelector('[data-recipe-next]');
      if (!track || !items.length) return;

      root.dataset.recipeBound = 'true';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const visibleItems = () => items.filter((item) => !item.hidden);

      const getStep = () => {
        const first = visibleItems()[0];
        if (!first) return track.clientWidth;
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const updateNav = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= maxScroll;
        const canScroll = maxScroll > 2;
        if (prevBtn) prevBtn.disabled = !canScroll || atStart;
        if (nextBtn) nextBtn.disabled = !canScroll || atEnd;
      };

      const scrollByStep = (direction) => {
        track.scrollBy({
          left: direction * getStep(),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      };

      const applyFilter = (tag) => {
        const needle = (tag || '').trim().toLowerCase();
        let visible = 0;

        items.forEach((item) => {
          const tags = (item.dataset.tags || '')
            .split(',')
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean);
          const match = !needle || tags.includes(needle);
          item.hidden = !match;
          if (match) visible += 1;
        });

        tabs.forEach((tab) => {
          const active = (tab.dataset.tag || '') === needle;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });

        if (empty) empty.hidden = visible > 0;
        track.scrollTo({ left: 0, behavior: 'auto' });
        window.requestAnimationFrame(updateNav);
      };

      prevBtn?.addEventListener('click', () => scrollByStep(-1));
      nextBtn?.addEventListener('click', () => scrollByStep(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateNav), { passive: true });
      window.addEventListener('resize', () => window.requestAnimationFrame(updateNav));

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => applyFilter(tab.dataset.tag || ''));
      });

      const activeTab = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
      applyFilter(activeTab?.dataset.tag || '');
    });
  }

  initRecipeSliders();
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section?.dataset) {
      section.querySelectorAll?.('[data-recipe-slider]').forEach((root) => {
        delete root.dataset.recipeBound;
      });
      if (section.matches?.('[data-recipe-slider]')) delete section.dataset.recipeBound;
    }
    initRecipeSliders(section);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-copy]');
    if (!button) return;

    event.preventDefault();
    const url = button.dataset.shareUrl || window.location.href;
    const label = button.querySelector('.social-share__copied');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }

      if (label) {
        label.hidden = false;
        window.setTimeout(() => {
          label.hidden = true;
        }, 1600);
      }
    } catch (error) {
      console.warn('Could not copy link', error);
    }
  });
})();
